import React, { useState, useEffect, Fragment } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Group from '@lightspeed/cirrus-group';
import Modal, { ModalBody, ModalFooter } from '@lightspeed/cirrus-modal';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Button } from '@lightspeed/flame/Button';
import { Divider } from '@lightspeed/flame/Divider';
import { Text, Heading3 } from '@lightspeed/flame/Text';
import { STATE } from 'pages/views/settings/PaymentProcessing/gateway/LightspeedPayments/GatewayConfig/index';

const StateImg = styled('img')`
  width: 300px;
`;

const PreWrapText = styled(Text)`
  white-space: pre-wrap;
`;

const statusTransformer = status => {
  let imgSrc;
  let title;
  let subtitle;

  switch (status) {
    case STATE.TEST.SUCCESS:
      imgSrc = 'https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/payments/p400/connection-success@2x.png';
      title = 'Connection successful';
      subtitle = 'Your payment terminal is connected and ready to take payments.';
      break;
    case STATE.TEST.FAIL:
      imgSrc = 'https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/payments/p400/connection-fail@2x.png';
      title = 'Connection failed';
      subtitle =
        'Your payment terminal could not be connected.\n Try again or contact our support team.';
      break;
    case STATE.TEST.IN_PROGRESS:
    default:
      imgSrc = 'https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/payments/p400/connection-test.gif';
      title = 'Connecting to terminal';
      subtitle = 'This should only take a few seconds.';
  }

  return { imgSrc, title, subtitle };
};

export const TestCCTerminalComponent = ({
  isOpen,
  isProcessing,
  status,
  errorMessage,
  onRetry,
  onSuccess,
}) => {
  const [viewDetails, setViewDetails] = useState(false);
  const { imgSrc, title, subtitle } = statusTransformer(status);

  useEffect(() => {
    if (status === STATE.TEST.IN_PROGRESS || isProcessing) {
      setViewDetails(false);
    }
  }, [status, isProcessing]);

  return (
    <Modal isOpen={isOpen}>
      <ModalBody>
        <div style={{ width: '520px' }}>
          <Flex flexDirection="column" alignItems="center" textAlign="center">
            <Box mt="4">
              <StateImg src={imgSrc} alt={title} />
            </Box>
            <Heading3 mt="2">{title}</Heading3>
            <Box mt="1">
              <PreWrapText>{subtitle}</PreWrapText>
            </Box>
            {errorMessage && (
              <Box mt="4">
                {viewDetails ? (
                  <Fragment>
                    <Divider />
                    <PreWrapText mt="4" size="small" color="gray-600">
                      {errorMessage}
                    </PreWrapText>
                  </Fragment>
                ) : (
                  <Button
                    data-test="retail-payments-testccterminal-viewdetails"
                    onClick={() => {
                      setViewDetails(true);
                    }}
                  >
                    View details
                  </Button>
                )}
              </Box>
            )}
          </Flex>
        </div>
      </ModalBody>
      <ModalFooter>
        <Group className="cr-justify-end">
          {status !== STATE.TEST.SUCCESS && (
            <Button
              onClick={onSuccess}
              data-test="retail-payments-testccterminal-cancel"
              disabled={status === STATE.TEST.IN_PROGRESS || isProcessing}
              fill
            >
              Cancel
            </Button>
          )}
          {status === STATE.TEST.FAIL && (
            <Button
              variant="secondary"
              type="submit"
              onClick={onRetry}
              loading={isProcessing}
              data-test="retail-payments-testccterminal-retry"
              fill
            >
              Try again
            </Button>
          )}
          {status === STATE.TEST.SUCCESS && (
            <Button
              variant="secondary"
              type="submit"
              onClick={onSuccess}
              loading={isProcessing}
              data-test="retail-payments-testccterminal-close"
              fill
            >
              Ok
            </Button>
          )}
        </Group>
      </ModalFooter>
    </Modal>
  );
};

TestCCTerminalComponent.propTypes = {
  isOpen: PropTypes.bool,
  onRetry: PropTypes.func,
  onSuccess: PropTypes.func,
  isProcessing: PropTypes.bool,
  status: PropTypes.string,
  errorMessage: PropTypes.string,
};

export default TestCCTerminalComponent;
